import { Suspense, lazy, Component, type ReactNode } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { AnimatePresence } from 'framer-motion';

class ErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
    constructor(props: { children: ReactNode }) {
        super(props);
        this.state = { hasError: false };
    }
    static getDerivedStateFromError() {
        return { hasError: true };
    }
    render() {
        if (this.state.hasError) {
            return (
                <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-8 text-center">
                    <p className="text-lg font-semibold">Algo deu errado ao carregar esta página.</p>
                    <button
                        className="underline text-primary"
                        onClick={() => { this.setState({ hasError: false }); window.location.reload(); }}
                    >
                        Tentar novamente
                    </button>
                </div>
            );
        }
        return this.props.children;
    }
}


// Lazy loading das páginas
const Login = lazy(() => import('./pages/Login'));
const Home = lazy(() => import('./pages/Home'));
const Onboarding = lazy(() => import('./pages/VideoOnboarding'));
const Anamnese = lazy(() => import('./pages/Anamnese'));
const Dieta = lazy(() => import('./pages/Dieta'));
const Treino = lazy(() => import('./pages/Treino'));
const TrocarSenha = lazy(() => import('./pages/TrocarSenha'));
const RedefinirSenha = lazy(() => import('./pages/RedefinirSenha'));
const AcessoBloqueado = lazy(() => import('./pages/AcessoBloqueado'));
const Perfil = lazy(() => import('./pages/Perfil'));
const PoliticaPrivacidade = lazy(() => import('./pages/PoliticaPrivacidade'));

const AdminLayout = lazy(() => import('./components/admin/AdminLayout'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'));

import { ProtectedLayout } from '@/components/layout/ProtectedLayout';
import { AuthInitializer } from '@/hooks/useAuth';

function AppRoutes() {
  const location = useLocation();
  const { user } = useAuth();

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        {/* Public Routes */}
        <Route path="/login" element={!user ? <Login /> : <Navigate to="/" />} />
        <Route path="/redefinir-senha" element={<RedefinirSenha />} />
        <Route path="/politica-de-privacidade" element={<PoliticaPrivacidade />} />

        {/* Admin Routes */}
        <Route element={<AdminLayout />}>
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/users" element={<AdminUsers />} />
        </Route>

        {/* Protected Routes (Wrapped in Persistent Layout) */}
        <Route element={<ProtectedLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/dieta" element={<Dieta />} />
          <Route path="/treino" element={<Treino />} />
          <Route path="/perfil" element={<Perfil />} />

          {/* Rotas de Onboarding e Fluxo */}
          <Route path="/onboarding" element={<Onboarding />} />
          <Route path="/intro-video" element={<Onboarding />} />
          <Route path="/trocar-senha" element={<TrocarSenha />} />

          <Route path="/bloqueado" element={<AcessoBloqueado />} />
          <Route path="/anamnese" element={<Anamnese />} />
        </Route>

        {/* Rota 404 — redireciona para home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AnimatePresence>
  );
}

const queryClient = new QueryClient();

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthInitializer />
        <ErrorBoundary>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Carregando...</div>}>
            <AppRoutes />
          </Suspense>
        </ErrorBoundary>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
